import {
  Chip,
  Icon,
  SquareDotLoadingIndicator,
  Text,
  TextWithLabel,
} from '@joor/design-system'
import styled from 'styled-components'

import { Attachment, OrderDiscountType } from '__generated__/atlas-types'

import { PaymentType } from '../SendPaymentRequestModal'
import { PaymentAmount } from '../sendPaymentRequest.hooks'
import DetailsPage1 from './DetailsPage1'
import DetailsPage2 from './DetailsPage2'

export const TOOLTIP_INFO_TEXT =
  'This information can only be edited from within order review.'

const OrderInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-flow: row;
  gap: 30px;
  margin-top: 20px;
`

const FullPaidWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
`

const LoaderWrapper = styled.div`
  display: flex;
  justify-items: center;
  align-items: center;
  height: 100%;
  width: 100%;
  margin-top: 50px;

  > * {
    margin: auto;
  }
`

const TextWithEllipsis = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap !important;
  max-width: 200px;
`

type DetailsTabProps = {
  orderInfo: {
    id?: string
    brandId?: string
    po?: string
    currency?: string
    retailerId?: string
    retailerName?: string
    retailerEmail?: string
    remainingAmount?: string
  }
  emails: Chip[]
  setEmails: (emails: Chip[]) => void
  onFieldError?: (isInvalid: boolean) => void
  currentStep: number
  emailSubject: string
  setEmailSubject: (emailSub: string) => void
  emailBody: string
  setEmailBody: (emailSub: string) => void
  attachment?: Attachment
  attachmentLoading?: boolean
  handleUploading: (attachment: Attachment) => void
  handleUpdateAttachmentLoading: (attachmentLoading: boolean) => void
  onDeleteAttachment: () => void
  paymentName: string
  setPaymentName: (paymentName: string) => void
  paymentAmount: { amount: string; format: OrderDiscountType }
  setPaymentAmount: (paymentAmount: PaymentAmount) => void
  dueDate?: string
  setDueDate: (dueDate: string) => void
  paymentType: PaymentType
  setPaymentType: (paymentType: PaymentType) => void
  loading: boolean
  onResetPayment: () => void
  onNumericInputFilled?: (value: string) => void
  selectedAmount?: string
}

const DetailsTab = ({
  orderInfo,
  emails,
  setEmails,
  onFieldError,
  currentStep,
  emailSubject,
  setEmailSubject,
  emailBody,
  setEmailBody,
  paymentName,
  setPaymentName,
  paymentAmount,
  setPaymentAmount,
  dueDate,
  setDueDate,
  paymentType,
  setPaymentType,
  attachment,
  attachmentLoading,
  handleUploading,
  handleUpdateAttachmentLoading,
  onDeleteAttachment,
  loading,
  onResetPayment,
  onNumericInputFilled,
  selectedAmount,
}: DetailsTabProps) => {
  const showWarningAlreadyPaid =
    parseFloat(orderInfo?.remainingAmount || '0') <= 0
  return (
    <div style={{ height: '250px' }}>
      <OrderInfoContainer>
        {orderInfo?.po && (
          <TextWithLabel label="Order #" caption={TOOLTIP_INFO_TEXT}>
            <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
              {orderInfo.po}
            </Text>
          </TextWithLabel>
        )}
        {orderInfo?.retailerName && (
          <TextWithLabel label="Retailer" caption={TOOLTIP_INFO_TEXT}>
            <TextWithEllipsis
              fontFamily="alpha"
              fontSize="small"
              fontWeight="medium"
            >
              {orderInfo?.retailerName}
            </TextWithEllipsis>
          </TextWithLabel>
        )}
        {orderInfo?.retailerEmail && (
          <TextWithLabel label="Retailer Email" caption={TOOLTIP_INFO_TEXT}>
            <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
              {orderInfo?.retailerEmail}
            </Text>
          </TextWithLabel>
        )}
        {currentStep === 1 && (
          <TextWithLabel label="Payment Amount" caption={TOOLTIP_INFO_TEXT}>
            <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
              {selectedAmount}
            </Text>
          </TextWithLabel>
        )}
        {currentStep === 1 && orderInfo?.currency && (
          <TextWithLabel label="Currency" caption={TOOLTIP_INFO_TEXT}>
            <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
              {orderInfo.currency}
            </Text>
          </TextWithLabel>
        )}
      </OrderInfoContainer>
      <div>
        {loading && (
          <LoaderWrapper>
            <SquareDotLoadingIndicator speed={1} />
          </LoaderWrapper>
        )}
        {currentStep === 0 && !loading && showWarningAlreadyPaid && (
          <FullPaidWrapper>
            <Icon iconName="warning" iconSize="regular" plain />
            <span>This order has no remaining balance to request.</span>
          </FullPaidWrapper>
        )}
        {currentStep === 0 && !loading && !showWarningAlreadyPaid && (
          <DetailsPage1
            orderEmail={orderInfo?.retailerEmail}
            emailChips={emails}
            setEmailChips={setEmails}
            onFieldError={onFieldError}
            remainingAmount={orderInfo?.remainingAmount}
            currency={orderInfo.currency}
            paymentName={paymentName}
            setPaymentName={setPaymentName}
            paymentAmount={paymentAmount}
            setPaymentAmount={setPaymentAmount}
            paymentType={paymentType}
            setPaymentType={setPaymentType}
            onResetPayment={onResetPayment}
            setNumericInputFilled={onNumericInputFilled}
            dueDate={dueDate}
            setDueDate={setDueDate}
          />
        )}
        {currentStep === 1 && (
          <DetailsPage2
            orderId={orderInfo.id}
            emailSubject={emailSubject}
            setEmailSubject={setEmailSubject}
            emailBody={emailBody}
            setEmailBody={setEmailBody}
            attachment={attachment}
            attachmentLoading={attachmentLoading}
            handleUploading={handleUploading}
            handleUpdateAttachmentLoading={handleUpdateAttachmentLoading}
            onDeleteAttachment={onDeleteAttachment}
            dueDate={dueDate || ''}
          />
        )}
      </div>
    </div>
  )
}

export default DetailsTab
